import { faArrowToBottom, faTable } from "@fortawesome/pro-light-svg-icons";
import { faPivotTable } from "extensions/fa5-icons/faPivotTable";
import { createRef, Component } from "react";
import { connect } from "react-redux";
import classNames from "classnames";
import moment from "moment";
import { webix } from "extensions/webix";
import styled from "styled-components";

import { pazienHistory } from "common/components/pazienHistory";
import { HelpHover } from "common/components/helpHover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "common/components/modal";
import { withTooltip } from "common/components/tooltip";
import { getQFilter } from "common/utils";

import { ErrorView } from "common/components/error";
import { ButtonSwitch, CancelButton } from "common/components/buttons";

import { SET_RECON_STORE, SET_SORT_OPTIONS } from "common/actions/reconciliation";

import { FilterBarRedux } from "common/components/filterBar/FilterBar";

import { Tabs } from "./tabs";

import { BatchTableRedux } from "./batchTable";
import { BatchDrilldown } from "./batchDrilldown";
import { BatchPivotTable } from "./batchPivotTable";
import { ExportDialog } from "./reconExport";

import "./reconciliation.scss";

const mapStateToProps = (state) => ({
    date_range: state.common.date_range,
    feature_export: state.permissions.user_permissions.feature_export,
    table_configs: state.components.table_configs,
    section: state.reconciliation.section,
    transferStatus: state.reconciliation.transferStatus,
    sortOptions: state.reconciliation.sortOptions,
    qualifiers: state.reconciliation.qualifiers,
    filters: state.reconciliation.filters,
    view: state.reconciliation.view,
    has_oct: state.common.merchant_capabilities.has_oct,
    merchant_dimensions: state.common.merchant_dimensions,
    haveAutomaticDownload: state.common.user.superuser
});

const mapDispatchToProps = {
    SET_RECON_STORE
};

export const Reconciliation = connect(mapStateToProps, mapDispatchToProps)(class extends Component {
    constructor(props) {
        super(props);
        this.state = {
            status: "idle",
            error: null,
            exportVisible: false
        };
        this.childElement = createRef();

        this.sections = [
            { name: "Net cash flow", alias: "all" },
            { name: this.props.has_oct ? "Sales, refunds and payouts" : "Sales and refunds", alias: "settlements" },
            { name: "Chargebacks", alias: "chargebacks" },
            { name: "Fees and taxes", alias: "fees" },
            { name: "Misc", alias: "misc" }
        ];
        this.transferStatuses = [
            { name: "Funds transferred", alias: "settled", info: "Funds transferred are monies that have been accepted for settlement and released to your bank. It can take 0-3+ days for the funds to be available in your bank account." },
            { name: "Awaiting transfer", alias: "accepted", info: "Awaiting transfers are monies that have been accepted for settlement, but not yet released to your bank." }
        ];
    }

    componentDidMount() {
        // Set url with display options
        const { displayOptions = "", qualifiers = "", queryParams: { q = "" } } = this.props.options;
        this.navigate({
            display: displayOptions,
            qualifiers,
            filters: displayOptions?.includes("view:txns") ? q : "", // reset q param in url in summary view
            trigger: false,
            replace: true
        });
        SET_SORT_OPTIONS({ key: this.getDateType(), order: -1 });
    }

    componentDidCatch(error) {
        this.setState({
            status: "error",
            error
        });
    }

    handleFilterBarUpdate = (qualifiers) => this.navigate({ qualifiers });

    hideExport = () => {
        this.setState({ exportVisible: false });
    };

    showExport = () => {
        this.setState({ exportVisible: true });
    };

    getDateType = () => (this.props.transferStatus === "settled" ? "settle_date" : "accepted_date");

    getExportOptions = () => {
        const filters = {
            start: this.props.date_range.startDate || "",
            end: this.props.date_range.endDate || "",
            status: this.props.transferStatus,
            sort: [[this.props.sortOptions.key, this.props.sortOptions.order === 1 ? "asc" : "desc"]],
            pivots: ["stl_currency", this.getDateType(), "acquirer", "funds_transfer_groupings"],
            datetype: "settlement", // keeping it for now
            q: this.props.qualifiers // qualifiers
        };

        // const totalTxns = this.state.count;
        const tempTransactionSectionsInfo = [
            { name: "Chargebacks", alias: "chargebacks" },
            { name: "Sales and refunds", alias: "settlements" },
            { name: "Fees and taxes", alias: "fees" },
            { name: "Misc", alias: "misc" }
        ];

        // Prioritize user configs in bulk txn export
        const txns_configs = {
            ...this.props.table_configs.transactions.pazien,
            ...this.props.table_configs.transactions.user
        };

        if (this.props.view === "pivot") {
            const tableChildren = this.childElement?.current?.childElement?.current?.ui?.getChildViews();

            if (tableChildren.length !== 2) {
                throw new Error("Cannot export from this pivot table");
            } else {
                return {
                    filters: {},
                    formats: [
                        {
                            active: true,
                            value: "xlsx",
                            label: "XLSX",
                            selected: true
                        }
                    ],
                    content: [
                        {
                            resource: "html",
                            name: "HTML",
                            structure: [{
                                value: webix.toHTML(tableChildren[1]).outerHTML,
                                structure: null
                            }]
                        }
                    ],
                    suggested_name: `reconciliation_${moment.utc(filters.start).format("YYYYMMDD")}-${moment.utc(filters.end).format("YYYYMMDD")}`
                };
            }
        }

        const configs = {
            ...this.props.table_configs.recon_summary.pazien,
            ...this.props.table_configs.recon_summary.user
        };

        return {
            filters,
            content: [
                {
                    name: "Summaries",
                    resource: "summaries_fisc_recon_v3",
                    structure: this.sections.map(({ name, alias }) => ({
                        name,
                        value: alias,
                        structure: {
                            values: [
                                "5d7181e6fcb2af8d3050b2c0", //  inject Flagged & Status(reconciled)
                                "5d7181e6fcb2af8d3050b2c3",
                                ...configs[alias].data.map(({ getter_ref }) => { // Handle accepted/settled date
                                    if (this.props.transferStatus === "accepted" && getter_ref === "5ec7dc5375c262cf16068b15") return "5ec7dc5375c262cf16068b02";
                                    if (this.props.transferStatus === "settled" && getter_ref === "5ec7dc5375c262cf16068b02") return "5ec7dc5375c262cf16068b15";
                                    return getter_ref;
                                })]
                        }
                    }))
                },
                {
                    name: "Transactions",
                    resource: "transactions_fisc_recon_v3",
                    structure: tempTransactionSectionsInfo.map(({ name, alias }) => ({
                        name,
                        value: alias,
                        structure: {
                            values: txns_configs[alias].data.map(({ getter_ref }) => getter_ref) // no collapsed support
                        }
                    }))
                }
            ],
            section: this.props.section,
            formats: [
                {
                    active: true,
                    value: "csv",
                    label: "CSV"
                },
                {
                    active: true, // totalTxns === null || totalTxns < 1048576,
                    value: "xlsx",
                    label: "XLSX"
                }
            ],
            suggested_name: `reconciliation_${moment.utc(filters.start).format("YYYYMMDD")}-${moment.utc(filters.end).format("YYYYMMDD")}`
        };
    };

    /**
     * Creates path segments to be used in navigation
     * @param {object} obj
     * @param {object} [obj.display] Describes the view and section
     * @param {object} [obj.filters]
     * @returns {object} Keys and values for `displayOptions` and `q`
     */
    buildPathSegments = ({
        display = {}, filters = null
    } = {}) => {
        const displayOptions = getQFilter({
            keys: ["view", "section", "transferStatus", "pivots"],
            obj: {
                ...this.props,
                ...display
            }
        });

        const q = typeof filters === "string" ? filters : this.props.filters;

        return { displayOptions, q };
    };

    navigate = ({
        display = {}, qualifiers = null, filters = null, trigger = false, replace = false
    } = {}) => {
        const { displayOptions, q } = this.buildPathSegments({ display, filters });

        SET_RECON_STORE({
            ...display,
            ...typeof qualifiers === "string" ? { qualifiers } : {},
            filters: q
        });

        pazienHistory.navigateWithParams(`reconciliation3/${displayOptions}/${qualifiers || this.props.qualifiers}`, {
            trigger,
            replace,
            extraParams: { q }
        });
    };

    /**
     * @param {object} obj
     * @param {any} [obj.display] Describes the view and section
     * @param {string | null} [obj.qualifiers]
     * @param {any} [obj.filters] // TODO determine this type
     * @param {boolean} [obj.trigger]
     * @param {boolean} [obj.replace]
     */
    buildHref = ({
        display = {}, qualifiers = null, filters = null, trigger = false, replace = false
    } = {}) => {
        const { displayOptions, q } = this.buildPathSegments({ display, filters });
        const { newFragment } = pazienHistory.buildFragment(`reconciliation3/${displayOptions}/${qualifiers || this.props.qualifiers}`, {
            trigger,
            replace,
            extraParams: { q }
        });
        return `/merchant/${newFragment}`;
    };

    renderSummary() {
        return (
            <div className="reconciliation root">
                <TransferStatusTabs data={this.transferStatuses} active={this.props.transferStatus} navigate={this.navigate} />
                <FilterBarRedux
                    sources={["acquirer", ["currency_alias", "stl_currency"], "flagged", "reconciled", "acquiring_entity"]}
                    overrideMode="merge"
                    sourcesOverride={{
                        data: {
                            reconciled: { "true": "Marked as reconciled" },
                            flagged: { "true": "Flagged for follow-up" }
                        },
                        meta: {
                            flagged: { pivot_alias: "Flagged" },
                            reconciled: { pivot_alias: "Reconciled" }
                        }
                    }}
                    qualifiers={this.props.qualifiers}
                    categoryModeAvailable={false}
                    onChange={(qualifiers) => this.handleFilterBarUpdate(qualifiers.toString(true))}
                />
                <Tabs sections={this.sections} active={this.props.section} selectTab={this.navigate}>
                    <div className="recon-controls">
                        <ButtonSwitch activeValue={this.props.view}>
                            <ButtonSwitch.Button value="flat" tooltip="Table view" onClick={() => this.navigate({ display: { view: "flat" } })} >
                                <FontAwesomeIcon icon={faTable} />
                            </ButtonSwitch.Button>
                            <ButtonSwitch.Button value="pivot" tooltip="Pivot table view" onClick={() => this.navigate({ display: { view: "pivot" } })} >
                                <FontAwesomeIcon icon={faPivotTable} />
                            </ButtonSwitch.Button >
                        </ButtonSwitch>
                        {this.props.feature_export && withTooltip(
                            (
                                <ExportButton onClick={this.showExport}>
                                    <FontAwesomeIcon icon={faArrowToBottom} />
                                </ExportButton>
                            ), { title: "Export" }
                        )}
                    </div>
                    {this.props.view === "pivot"
                        ? (
                            <BatchPivotTable
                                dateType={this.getDateType()}
                                navigate={this.navigate}
                                ref={this.childElement}
                            />
                        )
                        : (
                            <BatchTableRedux
                                className="summaries"
                                navigate={this.navigate}
                                dateType={this.getDateType()}
                                forceFullView
                                showControls
                                showExportButton={false}
                                paginatorTop
                                paginatorBottom
                                ref={this.childElement}
                            />
                        )}
                </Tabs>
                <Modal
                    close={this.hideExport}
                    header={this.props.view === "pivot" ? "Export pivot table view" : "Export data"}
                    portalId="modal-export-recon-pivot"
                    show={this.state.exportVisible}
                >
                    <ExportDialog
                        section={this.props.section}
                        getExportOptions={this.getExportOptions}
                        getSelected={this.childElement.current?.getSelectedSummaryFilters}
                        haveAutomaticDownload={this.props.haveAutomaticDownload}
                    />
                </Modal>
            </div>
        );
    }

    renderDrilldown = () => (
        <div className="reconciliation root">
            <BatchDrilldown
                sections={this.sections}
                qualifiers={this.props.qualifiers}
                queryParams={this.props.options.queryParams}
                navigate={this.navigate}
                dateType={this.getDateType()}
                ref={this.childElement}
                buildHref={this.buildHref}
            />
        </div>
    );

    render() {
        if (this.state.status === "error") {
            return <ErrorView error={this.state.error} inline={false} />;
        }

        if (this.props.view === "txns") {
            return this.renderDrilldown();
        }

        return this.renderSummary();
    }
});

function TransferStatusTabs(props) {
    return (
        <div className="transfer-status-tabs" >
            { props.data.map(({ name, alias, info }) => (
                <div
                    key={alias}
                    className={classNames("status-tab", { active: alias === props.active })}
                    onClick={props.navigate.bind(null, { display: { transferStatus: alias } })}
                >
                    <HelpHover solidIcon size="sm" topOffset="-7px" title={name} body={info}>
                        <div className="header">{name}</div>
                    </HelpHover>
                </div>
            ))}
        </div>
    );
}

const ExportButton = styled(CancelButton)`
    max-height: 32px;
    margin: 0 0 0 6px;

    svg {
        margin: 0;
        height: 18px;

    }
`;
